import { Injectable, computed, signal } from '@angular/core';
import { Bucket, Group, GroupPage } from '../types';
import { CreateBucketRequest } from '../generated-api/models';

export interface BudgetBulkEditTotals {
  income: number;
  expenses: number;
  net: number;
}

export interface BudgetBulkEditBucketUpdate {
  id: string;
  name: string;
  amount: number;
}

export interface BudgetBulkEditArchiveDraft {
  bucketId: string;
  transferBalanceBucketId?: string;
}

export interface BudgetBulkEditGroupReorderDraft {
  groupId: string;
  sortIndex: number;
}

export interface BudgetBulkEditBucketReorderDraft {
  bucketId: string;
  groupId: string;
  sortIndex: number;
}

export interface BudgetBulkEditPendingBucket {
  id: string;
  groupId: string;
  monthYear: string;
  name: string;
  amount: number;
  type: 'INCOME' | 'BUDGET' | 'SAVINGS' | 'TRANSFERS';
}

export interface BudgetBulkEditPendingGroup {
  id: string;
  name: string;
}

interface BudgetBulkEditState {
  basePage: GroupPage | null;
  bucketUpdates: Record<string, BudgetBulkEditBucketUpdate>;
  groupRenames: Record<string, string>;
  pendingGroups: BudgetBulkEditPendingGroup[];
  pendingBuckets: BudgetBulkEditPendingBucket[];
  archiveDrafts: Record<string, BudgetBulkEditArchiveDraft>;
  unarchiveDrafts: Record<string, string>;
  groupReorders: Record<string, BudgetBulkEditGroupReorderDraft>;
  bucketReorders: Record<string, BudgetBulkEditBucketReorderDraft>;
  /** Groups whose budget order currently differs from the saved order. */
  bucketOrderChanged: Record<string, true>;
}

@Injectable({
  providedIn: 'root',
})
export class BudgetBulkEditService {
  private nextPendingBucketId = 0;
  private nextPendingGroupId = 0;
  private readonly state = signal<BudgetBulkEditState>({
    basePage: null,
    bucketUpdates: {},
    groupRenames: {},
    pendingGroups: [],
    pendingBuckets: [],
    archiveDrafts: {},
    unarchiveDrafts: {},
    groupReorders: {},
    bucketReorders: {},
    bucketOrderChanged: {},
  });

  readonly totals = computed<BudgetBulkEditTotals>(() => {
    const state = this.state();
    if (!state.basePage) return { income: 0, expenses: 0, net: 0 };

    const buckets = this.getPreviewBuckets(state);
    const income = this.round(
      buckets.filter(bucket => bucket.type === 'INCOME').reduce((total, bucket) => total + bucket.amount, 0),
    );
    const expenses = this.round(
      buckets
        .filter(bucket => bucket.type !== 'INCOME' && bucket.type !== 'TRANSFERS')
        .reduce((total, bucket) => total + bucket.amount, 0),
    );

    return {
      income,
      expenses,
      net: this.round(income - expenses),
    };
  });

  /** Whether Save would write anything; Cancel only needs to confirm when this is true. */
  readonly hasChanges = computed(() => {
    const state = this.state();
    if (!state.basePage) return false;
    return (
      this.changedBucketUpdatesFor(state).length > 0 ||
      this.changedGroupRenamesFor(state).length > 0 ||
      state.pendingGroups.length > 0 ||
      state.pendingBuckets.length > 0 ||
      Object.keys(state.archiveDrafts).length > 0 ||
      Object.keys(state.unarchiveDrafts).length > 0 ||
      Object.keys(state.groupReorders).length > 0 ||
      Object.keys(state.bucketReorders).length > 0
    );
  });

  hasDraft(): boolean {
    return !!this.state().basePage;
  }

  begin(groupPage: GroupPage): void {
    this.state.set({
      basePage: groupPage,
      bucketUpdates: {},
      groupRenames: {},
      pendingGroups: [],
      pendingBuckets: [],
      archiveDrafts: {},
      unarchiveDrafts: {},
      groupReorders: {},
      bucketReorders: {},
      bucketOrderChanged: {},
    });
  }

  discard(): void {
    this.state.set({
      basePage: null,
      bucketUpdates: {},
      groupRenames: {},
      pendingGroups: [],
      pendingBuckets: [],
      archiveDrafts: {},
      unarchiveDrafts: {},
      groupReorders: {},
      bucketReorders: {},
      bucketOrderChanged: {},
    });
  }

  updateBucket(bucket: Bucket, changes: { name?: string; amount?: number | null }): void {
    const current = this.state();
    const existing = current.bucketUpdates[bucket.id] ?? {
      id: bucket.id,
      name: bucket.name,
      amount: bucket.amount,
    };
    const nextUpdate = {
      ...existing,
      ...(changes.name !== undefined ? { name: changes.name } : {}),
      ...(changes.amount !== undefined ? { amount: changes.amount ?? 0 } : {}),
    };

    this.state.set({
      ...current,
      bucketUpdates: {
        ...current.bucketUpdates,
        [bucket.id]: nextUpdate,
      },
    });
  }

  draftBucketName(bucket: Bucket): string {
    return this.state().bucketUpdates[bucket.id]?.name ?? bucket.name;
  }

  draftBucketAmount(bucket: Bucket): number {
    return this.state().bucketUpdates[bucket.id]?.amount ?? bucket.amount;
  }

  renameGroup(groupId: string, name: string): void {
    const current = this.state();
    this.state.set({
      ...current,
      groupRenames: {
        ...current.groupRenames,
        [groupId]: name,
      },
    });
  }

  draftGroupName(group: Pick<Group, 'id' | 'name'>): string {
    return this.state().groupRenames[group.id] ?? group.name;
  }

  /** The name Save sends: a blank rename keeps the saved name instead of writing "". */
  savedGroupName(group: Pick<Group, 'id' | 'name'>): string {
    return this.state().groupRenames[group.id]?.trim() || group.name;
  }

  changedGroupRenames(): string[] {
    return this.changedGroupRenamesFor(this.state());
  }

  addPendingBucket(group: Group, monthYear: string): BudgetBulkEditPendingBucket {
    const pendingBucket: BudgetBulkEditPendingBucket = {
      id: `pending-budget-${++this.nextPendingBucketId}`,
      groupId: group.id,
      monthYear,
      name: 'New Budget',
      amount: 0,
      type: this.isIncomeGroup(group) ? 'INCOME' : 'BUDGET',
    };
    const current = this.state();
    this.state.set({
      ...current,
      pendingBuckets: [...current.pendingBuckets, pendingBucket],
    });
    return pendingBucket;
  }

  addPendingGroup(): BudgetBulkEditPendingGroup {
    const pendingGroup: BudgetBulkEditPendingGroup = {
      id: `pending-group-${++this.nextPendingGroupId}`,
      name: 'New Group',
    };
    const current = this.state();
    this.state.set({
      ...current,
      pendingGroups: [pendingGroup, ...current.pendingGroups],
    });
    return pendingGroup;
  }

  updatePendingGroup(id: string, name: string): void {
    const current = this.state();
    this.state.set({
      ...current,
      pendingGroups: current.pendingGroups.map(group => (group.id === id ? { ...group, name } : group)),
    });
  }

  removePendingGroup(id: string): void {
    const current = this.state();
    this.state.set({
      ...current,
      pendingGroups: current.pendingGroups.filter(group => group.id !== id),
      pendingBuckets: current.pendingBuckets.filter(bucket => bucket.groupId !== id),
    });
  }

  pendingGroups(): BudgetBulkEditPendingGroup[] {
    return this.state().pendingGroups;
  }

  updatePendingBucket(
    id: string,
    changes: { name?: string; amount?: number | null; type?: BudgetBulkEditPendingBucket['type'] },
  ): void {
    const current = this.state();
    this.state.set({
      ...current,
      pendingBuckets: current.pendingBuckets.map(bucket =>
        bucket.id === id
          ? {
              ...bucket,
              ...(changes.name !== undefined ? { name: changes.name } : {}),
              ...(changes.amount !== undefined ? { amount: changes.amount ?? 0 } : {}),
              ...(changes.type !== undefined ? { type: changes.type } : {}),
            }
          : bucket,
      ),
    });
  }

  removePendingBucket(id: string): void {
    const current = this.state();
    this.state.set({
      ...current,
      pendingBuckets: current.pendingBuckets.filter(bucket => bucket.id !== id),
    });
  }

  pendingBucketsForGroup(groupId: string): BudgetBulkEditPendingBucket[] {
    return this.state().pendingBuckets.filter(bucket => bucket.groupId === groupId);
  }

  markArchive(bucketId: string, transferBalanceBucketId?: string): void {
    const current = this.state();
    const { [bucketId]: _unarchived, ...unarchiveDrafts } = current.unarchiveDrafts;
    this.state.set({
      ...current,
      unarchiveDrafts,
      archiveDrafts: {
        ...current.archiveDrafts,
        [bucketId]: { bucketId, transferBalanceBucketId },
      },
    });
  }

  undoArchive(bucketId: string): void {
    const current = this.state();
    const { [bucketId]: _removed, ...archiveDrafts } = current.archiveDrafts;
    this.state.set({
      ...current,
      archiveDrafts,
    });
  }

  markUnarchive(bucketId: string): void {
    const current = this.state();
    const { [bucketId]: _archived, ...archiveDrafts } = current.archiveDrafts;
    this.state.set({
      ...current,
      archiveDrafts,
      unarchiveDrafts: {
        ...current.unarchiveDrafts,
        [bucketId]: bucketId,
      },
    });
  }

  undoUnarchive(bucketId: string): void {
    const current = this.state();
    const { [bucketId]: _removed, ...unarchiveDrafts } = current.unarchiveDrafts;
    this.state.set({
      ...current,
      unarchiveDrafts,
    });
  }

  isPendingArchive(bucketId: string): boolean {
    return !!this.state().archiveDrafts[bucketId];
  }

  isPendingUnarchive(bucketId: string): boolean {
    return !!this.state().unarchiveDrafts[bucketId];
  }

  archiveDraft(bucketId: string): BudgetBulkEditArchiveDraft | undefined {
    return this.state().archiveDrafts[bucketId];
  }

  changedBucketUpdates(): BudgetBulkEditBucketUpdate[] {
    return this.changedBucketUpdatesFor(this.state());
  }

  private changedBucketUpdatesFor(state: BudgetBulkEditState): BudgetBulkEditBucketUpdate[] {
    return Object.values(state.bucketUpdates).filter(update => {
      if (state.archiveDrafts[update.id]) return false;
      const base = this.findBaseBucket(state.basePage, update.id);
      return !!base && (base.name !== update.name || base.amount !== update.amount);
    });
  }

  pendingBucketCreates(groupIdMap: Record<string, string> = {}): CreateBucketRequest[] {
    return this.state().pendingBuckets.map(bucket => ({
      name: bucket.name,
      type: bucket.type,
      amount: bucket.amount,
      groupId: this.resolveCreateBucketGroupId(bucket.groupId, groupIdMap),
      fund: {
        start: bucket.monthYear,
        initialAmount: 0,
      },
      isSetup: true,
    }));
  }

  pendingArchives(): BudgetBulkEditArchiveDraft[] {
    return Object.values(this.state().archiveDrafts);
  }

  pendingUnarchives(): string[] {
    return Object.values(this.state().unarchiveDrafts);
  }

  reorderGroup(groupId: string, sortIndex: number): void {
    const current = this.state();
    this.state.set({
      ...current,
      groupReorders: {
        ...current.groupReorders,
        [groupId]: { groupId, sortIndex },
      },
    });
  }

  reorderBucket(bucketId: string, groupId: string, sortIndex: number): void {
    const current = this.state();
    this.state.set({
      ...current,
      bucketReorders: {
        ...current.bucketReorders,
        [bucketId]: { bucketId, groupId, sortIndex },
      },
    });
  }

  /**
   * Call after a group drag with the full rendered group order. Dragging a group
   * away and back restores the saved order, so it is no longer a change.
   */
  syncGroupOrder(orderedGroupIds: string[]): void {
    const current = this.state();
    if (!current.basePage || !Object.keys(current.groupReorders).length) return;
    const saved = current.basePage.data.map(group => group.id).filter(id => orderedGroupIds.includes(id));
    const rendered = orderedGroupIds.filter(id => saved.includes(id));
    if (!sameOrder(saved, rendered)) return;
    this.state.set({ ...current, groupReorders: {} });
  }

  /**
   * Call after a budget drag with the rendered budget order of every group the
   * drag touched (source and target). Once every group is back to its saved
   * order, the budget reorders are dropped.
   */
  syncBucketOrder(orderedBucketIdsByGroup: Record<string, string[]>): void {
    const current = this.state();
    if (!current.basePage) return;
    const bucketOrderChanged = { ...current.bucketOrderChanged };
    for (const [groupId, orderedBucketIds] of Object.entries(orderedBucketIdsByGroup)) {
      const saved = (current.basePage.data.find(group => group.id === groupId)?.buckets ?? [])
        .filter(bucket => bucket.type !== 'TRANSFERS')
        .map(bucket => bucket.id);
      if (sameOrder(saved, orderedBucketIds)) delete bucketOrderChanged[groupId];
      else bucketOrderChanged[groupId] = true;
    }
    const restored = !Object.keys(bucketOrderChanged).length;
    this.state.set({
      ...current,
      bucketOrderChanged,
      bucketReorders: restored ? {} : current.bucketReorders,
    });
  }

  pendingGroupReorders(): BudgetBulkEditGroupReorderDraft[] {
    return Object.values(this.state().groupReorders);
  }

  pendingBucketReorders(): BudgetBulkEditBucketReorderDraft[] {
    return Object.values(this.state().bucketReorders);
  }

  private getPreviewBuckets(state: BudgetBulkEditState): Array<Pick<Bucket, 'id' | 'amount' | 'type'>> {
    const baseBuckets =
      state.basePage?.data.flatMap(group =>
        group.buckets
          .filter(bucket => !bucket.archivedMonth || !!state.unarchiveDrafts[bucket.id])
          .filter(bucket => !state.archiveDrafts[bucket.id])
          .map(bucket => ({
            id: bucket.id,
            amount: state.bucketUpdates[bucket.id]?.amount ?? bucket.amount,
            type: bucket.type,
          })),
      ) ?? [];

    return [
      ...baseBuckets,
      ...state.pendingBuckets.map(bucket => ({
        id: bucket.id,
        amount: bucket.amount,
        type: bucket.type,
      })),
    ];
  }

  private changedGroupRenamesFor(state: BudgetBulkEditState): string[] {
    return Object.entries(state.groupRenames)
      .filter(([groupId, name]) => {
        const base = state.basePage?.data.find(group => group.id === groupId);
        const trimmed = name.trim();
        return !!base && !!trimmed && base.name !== trimmed;
      })
      .map(([groupId]) => groupId);
  }

  private findBaseBucket(groupPage: GroupPage | null, bucketId: string): Bucket | null {
    return groupPage?.data.flatMap(group => group.buckets).find(bucket => bucket.id === bucketId) ?? null;
  }

  private isIncomeGroup(group: Group): boolean {
    // Same rule as before, but summed from raw buckets instead of
    // `group.budgeted.BUDGET`: that map omits buckets excluded from spending
    // reports, so a group whose every expense bucket is excluded would look
    // income-only and default a new bucket to INCOME. This picks a bucket type
    // that gets persisted, so a reporting setting must not reach it.
    const buckets = group.buckets ?? [];
    const sumAmount = (type: Bucket['type']) =>
      buckets.filter(bucket => bucket.type === type).reduce((total, bucket) => total + bucket.amount, 0);
    return sumAmount('INCOME') > 0 && sumAmount('BUDGET') <= 0;
  }

  private resolveCreateBucketGroupId(groupId: string, groupIdMap: Record<string, string>): string | null {
    const resolvedGroupId = groupIdMap[groupId] ?? groupId;
    return resolvedGroupId === 'UNCATEGORIZED' ? null : resolvedGroupId;
  }

  private round(amount: number): number {
    return Math.round(amount * 100) / 100;
  }
}

function sameOrder(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, index) => id === b[index]);
}
